import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { motion } from 'framer-motion';
import { Users, Eye, Edit, CheckCircle, XCircle } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AcceptInvite = () => {
  const { token } = useParams();
  const navigate = useNavigate();
  const [invitation, setInvitation] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [accepting, setAccepting] = useState(false);
  const [isLoggedIn, setIsLoggedIn] = useState(false);

  useEffect(() => {
    // Check if user is logged in
    const authToken = localStorage.getItem('token');
    setIsLoggedIn(!!authToken);

    // Fetch invitation details
    fetchInvitation();
  }, [token]);

  const fetchInvitation = async () => {
    try {
      const response = await axios.get(`${API}/invitations/${token}`);
      setInvitation(response.data);
    } catch (error) {
      console.error('Error fetching invitation:', error);
      if (error.response?.status === 410) {
        setError('This invitation has expired.');
      } else if (error.response?.status === 404) {
        setError('Invitation not found.');
      } else {
        setError('Failed to load invitation details.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleAccept = async () => {
    if (!isLoggedIn) {
      toast.info('Please log in to accept this invitation');
      navigate('/login', { state: { returnTo: `/accept-invite/${token}` } });
      return;
    }

    setAccepting(true);
    try {
      const authToken = localStorage.getItem('token');
      await axios.post(
        `${API}/invitations/${token}/accept`,
        {},
        {
          headers: { Authorization: `Bearer ${authToken}` },
        }
      );
      toast.success('Invitation accepted! You now have access to the patient data.');
      navigate('/');
    } catch (error) {
      console.error('Error accepting invitation:', error);
      if (error.response?.status === 400) {
        toast.error(error.response?.data?.detail || 'Failed to accept invitation');
      } else {
        toast.error('Failed to accept invitation');
      }
    } finally {
      setAccepting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#FAFAFA]">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 mx-auto mb-4" style={{ borderColor: '#A2DAD3' }}></div>
          <p className="text-gray-600">Loading invitation...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#FAFAFA] p-4">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="card max-w-md w-full text-center"
        >
          <XCircle size={64} className="mx-auto mb-4" style={{ color: '#F9A7A7' }} />
          <h1 className="text-2xl font-bold mb-2" style={{ color: '#F9A7A7' }}>
            Invalid Invitation
          </h1>
          <p className="text-gray-600 mb-6">{error}</p>
          <button onClick={() => navigate('/')} className="btn btn-primary">
            Go to Dashboard
          </button>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FAFAFA] p-4 flex items-center justify-center">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="card max-w-lg w-full"
      >
        <div className="text-center mb-6">
          <Users size={64} className="mx-auto mb-4" style={{ color: '#A2DAD3' }} />
          <h1 className="text-2xl font-bold mb-2" style={{ color: '#333' }}>
            You've Been Invited!
          </h1>
          <p className="text-gray-600">
            {invitation.shared_by} wants to share patient health information with you
          </p>
        </div>

        <div className="space-y-4 mb-6">
          {/* Patient Info */}
          <div className="bg-gray-50 p-4 rounded-lg">
            <p className="text-sm text-gray-600 mb-1">Patient</p>
            <p className="text-lg font-semibold" style={{ color: '#A2DAD3' }}>
              {invitation.patient_name}
            </p>
          </div>

          {/* Access Level */}
          <div className="bg-gray-50 p-4 rounded-lg">
            <p className="text-sm text-gray-600 mb-2">Access Level</p>
            <div className="flex items-center gap-2">
              {invitation.invitation.permissions === 'view' ? (
                <>
                  <Eye size={20} style={{ color: '#A2DAD3' }} />
                  <span className="font-medium">View Only</span>
                </>
              ) : (
                <>
                  <Edit size={20} style={{ color: '#F9A7A7' }} />
                  <span className="font-medium">Can Edit</span>
                </>
              )}
            </div>
          </div>

          {/* Data Access */}
          <div className="bg-gray-50 p-4 rounded-lg">
            <p className="text-sm text-gray-600 mb-2">You'll have access to:</p>
            <div className="flex flex-wrap gap-2">
              {invitation.invitation.data_to_share.map((dataType) => (
                <span
                  key={dataType}
                  className="px-3 py-1 rounded-full text-sm"
                  style={{ backgroundColor: '#A2DAD3', color: 'white' }}
                >
                  {dataType.replace('_', ' ').replace(/\b\w/g, (l) => l.toUpperCase())}
                </span>
              ))}
            </div>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="space-y-3">
          {!isLoggedIn ? (
            <>
              <button
                onClick={() => navigate('/login', { state: { returnTo: `/accept-invite/${token}` } })}
                className="btn btn-primary w-full flex items-center justify-center gap-2"
              >
                <CheckCircle size={20} />
                Log In to Accept
              </button>
              <p className="text-sm text-center text-gray-600">
                Don't have an account?{' '}
                <button
                  onClick={() => navigate('/login', { state: { returnTo: `/accept-invite/${token}`, signup: true } })}
                  className="font-medium"
                  style={{ color: '#A2DAD3' }}
                >
                  Sign Up
                </button>
              </p>
            </>
          ) : (
            <>
              <button
                onClick={handleAccept}
                disabled={accepting}
                className="btn btn-primary w-full flex items-center justify-center gap-2"
              >
                <CheckCircle size={20} />
                {accepting ? 'Accepting...' : 'Accept Invitation'}
              </button>
              <button
                onClick={() => navigate('/')}
                className="btn w-full"
                style={{ backgroundColor: 'white', border: '1px solid #ddd' }}
              >
                Decline
              </button>
            </>
          )}
        </div>
      </motion.div>
    </div>
  );
};

export default AcceptInvite;
